import React from 'react';
import { Box, BoxProps, Flex, FlexProps, Text, Tooltip } from '@chakra-ui/react';

/**
 * Card padrão dos quadros kanban (29/09): a anatomia comum do Estatuto e dos Eventos, em
 * peças. O meio do card é de cada módulo; moldura, título, selos e rodapé são iguais.
 *
 *   ┌────────────────────────────────┐
 *   │ Título (2 linhas, largura toda) │
 *   │ subtítulo                       │
 *   │ (selo) (selo) (selo)            │  ← linha própria: ao lado do título, o selo de
 *   │ … conteúdo do módulo …          │    prazo do Estatuto corta o título na coluna
 *   │ ─────────────────────────────── │
 *   │ quem                 contadores │
 *   └────────────────────────────────┘
 *
 * Hoje só a legalização usa. O card do Estatuto e o genérico (Eventos, WhatsApp) podem
 * migrar sem mudar de cara.
 */

interface KanbanCardMolduraProps extends BoxProps {
  /** Faixa de 3px à esquerda, na cor do destaque (risco, novo, pronto). Sem destaque, sem faixa. */
  faixa?: string | null;
}

/**
 * ⚠️ Não vira <button>: no quadro, a alça do arraste é o wrapper do Draggable em volta, e o
 * @hello-pangea/dnd não inicia arrasto a partir de elemento interativo. O foco e o Enter
 * ficam nesse wrapper (que o dnd já deixa focável), não aqui — senão são duas paradas de Tab.
 */
export const KanbanCardMoldura: React.FC<KanbanCardMolduraProps> = ({
  faixa = null,
  children,
  ...resto
}) => (
  <Box
    w="100%"
    textAlign="left"
    bg="white"
    borderRadius="xl"
    borderWidth="1px"
    borderColor="gray.200"
    borderLeftWidth={faixa ? '3px' : '1px'}
    borderLeftColor={faixa ?? 'gray.200'}
    p={3}
    // A faixa engrossa a borda em 2px: o conteúdo não pode andar junto.
    pl={faixa ? '10px' : 3}
    boxShadow="0 1px 2px rgba(15,23,42,.05)"
    cursor={resto.onClick ? 'pointer' : 'default'}
    transition="border-color .15s, box-shadow .15s, transform .15s"
    _hover={
      resto.onClick
        ? {
            borderColor: 'blue.300',
            // `borderColor` pinta os 4 lados: sem isto a faixa ficaria azul no hover.
            borderLeftColor: faixa ?? 'blue.300',
            boxShadow: '0 4px 12px -2px rgba(15,23,42,.10)',
            transform: 'translateY(-1px)',
          }
        : undefined
    }
    {...resto}
  >
    {children}
  </Box>
);

export const KanbanCardTitulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fontSize="13px" fontWeight="600" color="gray.800" lineHeight="1.35" noOfLines={2}>
    {children}
  </Text>
);

export const KanbanCardSubtitulo: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    fontSize="11px"
    color="gray.500"
    mt={0.5}
    noOfLines={1}
    sx={{ fontVariantNumeric: 'tabular-nums' }}
  >
    {children}
  </Text>
);

/** Linha de selos: estado (prazo) primeiro, qualificadores depois; quebra se não couber. */
export const KanbanCardSelos: React.FC<FlexProps> = ({ children, ...resto }) => (
  <Flex align="center" gap={1.5} flexWrap="wrap" rowGap={1} mt={2} {...resto}>
    {children}
  </Flex>
);

interface KanbanSeloProps {
  children: React.ReactNode;
  /** Cor do texto e da bolinha. */
  cor?: string;
  bg?: string;
  /** Bolinha na cor do texto — para ESTADO (prazo, situação), não para qualificador. */
  ponto?: boolean;
  /** Só contorno — qualificador neutro (rota, tipo). */
  contorno?: boolean;
  dica?: string | null;
}

export const KanbanSelo: React.FC<KanbanSeloProps> = ({
  children,
  cor = 'gray.600',
  bg = 'gray.100',
  ponto = false,
  contorno = false,
  dica = null,
}) => {
  const selo = (
    <Flex
      as="span"
      display="inline-flex"
      align="center"
      gap={1}
      h="20px"
      px={2}
      borderRadius="full"
      bg={contorno ? 'transparent' : bg}
      color={cor}
      boxShadow={contorno ? 'inset 0 0 0 1px var(--chakra-colors-gray-300)' : undefined}
      fontSize="10px"
      fontWeight="600"
      lineHeight="1"
      whiteSpace="nowrap"
      cursor={dica ? 'default' : undefined}
      sx={{ fontVariantNumeric: 'tabular-nums' }}
    >
      {ponto && <Box as="span" w="6px" h="6px" borderRadius="full" bg={cor} flexShrink={0} />}
      {children}
    </Flex>
  );
  return dica ? (
    <Tooltip label={dica} hasArrow openDelay={300}>
      {selo}
    </Tooltip>
  ) : (
    selo
  );
};

/** Rodapé com linha fina: quem (esquerda) e contadores (direita), como no Estatuto e nos Eventos. */
export const KanbanCardRodape: React.FC<{
  esquerda?: React.ReactNode;
  direita?: React.ReactNode;
}> = ({ esquerda, direita }) => (
  <Flex
    align="center"
    justify="space-between"
    gap={2}
    mt={2.5}
    pt={2}
    borderTop="1px solid"
    borderColor="gray.100"
  >
    <Flex align="center" gap={1.5} minW={0} flex="1">
      {esquerda}
    </Flex>
    <Flex align="center" gap={2.5} flexShrink={0}>
      {direita}
    </Flex>
  </Flex>
);

/** Contador do rodapé: ícone + número, com a explicação no tooltip. */
export const KanbanCardInfo: React.FC<{
  icone: React.ReactNode;
  children: React.ReactNode;
  dica?: string;
  cor?: string;
}> = ({ icone, children, dica, cor = 'gray.400' }) => {
  const info = (
    <Flex as="span" display="inline-flex" align="center" gap={1} color={cor}>
      {icone}
      <Text
        as="span"
        fontSize="10px"
        fontWeight="600"
        whiteSpace="nowrap"
        sx={{ fontVariantNumeric: 'tabular-nums' }}
      >
        {children}
      </Text>
    </Flex>
  );
  return dica ? (
    <Tooltip label={dica} hasArrow openDelay={300}>
      {info}
    </Tooltip>
  ) : (
    info
  );
};
